import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';
import { useState } from 'react';
import { Else, If, Then } from 'react-if';

import AsyncButton from 'components/form/AsyncButton';

interface Price {
  id: string;
  nickname?: string;
  unitAmount?: number;
  currency?: string;
  recurring?: {
    interval?: 'month' | 'year';
  };
}

interface SubscriptionPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SubscriptionPlansModal({
  isOpen,
  onClose,
}: SubscriptionPlansModalProps): JSX.Element | null {
  const [plans, setPlans] = useState<Price[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function fetchPlans(): Promise<void> {
    setLoading(true);
    const { data } = await axios.get<Price[]>('/api/billing/subscription/prices');
    setPlans(data);
    setLoading(false);
    setLoaded(true);
  }

  if (!isOpen) {
    return null;
  }

  if (!loaded && !loading && !error) {
    void fetchPlans();
  }

  async function handleSelectPlan(priceId: string): Promise<void> {
    const { data } = await axios.post<{ url: string }>(
      '/api/billing/subscription/checkout-session',
      { priceId },
    );
    window.location.href = data.url;
  }

  return (
    <Modal isOpen onClose={onClose} size='2xl'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent borderRadius='12px' boxShadow='0 4px 12px rgba(0, 0, 0, 0.15)'>
        <ModalHeader borderBottom='1px solid #E2E8F0' pb='4'>
          <Text fontSize='xl' fontWeight='bold'>
            Choose Your Skyline Subscription
          </Text>
        </ModalHeader>
        <ModalCloseButton size='lg' />

        <ModalBody py='6' px='8'>
          <If condition={loading}>
            <Then>
              <Flex justifyContent='center'>
                <Spinner />
              </Flex>
            </Then>
            <Else>
              <If condition={Boolean(error)}>
                <Then>
                  <Text color='red.600' mb='4'>
                    {error}
                  </Text>
                </Then>
                <Else>
                  <If condition={plans.length > 0}>
                    <Then>
                      <Flex flexDirection='column' gap='4'>
                        {plans.map((plan) => {
                          const amount = ((plan.unitAmount ?? 0) / 100).toFixed(2);
                          const interval = plan.recurring?.interval === 'year' ? 'year' : 'month';

                          return (
                            <Flex
                              key={plan.id}
                              alignItems='center'
                              justifyContent='space-between'
                              border='1px solid #E2E8F0'
                              borderRadius='8px'
                              p='4'
                            >
                              <Flex flexDirection='column'>
                                <Text fontSize='md' fontWeight='bold'>
                                  {plan.nickname ?? 'Untitled Plan'}
                                </Text>
                                <Text fontSize='sm'>
                                  {amount} {plan.currency?.toUpperCase()}/{interval}
                                </Text>
                              </Flex>

                              <AsyncButton onClick={() => handleSelectPlan(plan.id)}>
                                Select Plan
                              </AsyncButton>
                            </Flex>
                          );
                        })}
                      </Flex>
                    </Then>
                    <Else>
                      <Text>No subscription plans available.</Text>
                    </Else>
                  </If>
                </Else>
              </If>
            </Else>
          </If>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
